<template>
  <!-- Fixed navbar -->
  <nav class="navbar navbar-default navbar-fixed-top">
    <GridContainer class="grid-container">
      <div class="navbar-header">
        <button
          type="button"
          class="navbar-toggle collapsed"
          data-toggle="collapse"
          data-target="#navbar"
          aria-expanded="false"
          aria-controls="navbar"
          style="color: var(--light-primary)"
        >
          <span class="sr-only">Toggle navigation</span>
          <i class="glyphicon glyphicon-menu-hamburger" />
        </button>
        <a class="navbar-brand" href="/">
          <img
            src="/img/apimetrics-context-logo-transparent.svg"
            alt="APImetrics by API Context"
            title="APImetrics by API Context"
            style="height: 57px"
          />
          <span
            v-if="showFractal"
            class="hidden-xs"
            style="margin-left: 32px; vertical-align: bottom"
          >
            Powered by
          </span>
          <img
            v-if="showFractal"
            v-tooltip="'Powered by tomato pay'"
            src="/img/tomatopay-logo.png"
            alt="Powered by tomato pay"
            title="Powered by tomato pay"
            class="hidden-xs"
            style="height: 32px; display: inline; vertical-align: bottom; margin-left: 8px"
          />
        </a>
      </div>

      <div id="navbar" class="navbar-collapse collapse">
        <ul class="nav navbar-nav navbar-right">
          <li
            v-if="showUpgrade"
            style="margin-top: 10px; padding-right: 12px"
            @show="
              {
                setShowUpgrade;
              }
            "
          >
            <UpgradeButton />
          </li>
          <li v-if="loaded">
            <div class="nav-container">
              <div v-if="isSiteViewer" class="sidebar">
                <AdminProjectPicker v-if="isSiteViewer" :org-id="projectOrgId || ''" />
              </div>
              <ProjectPicker v-if="loaded && projectsLoaded" />
              <div class="header-right-components">
                <div v-if="loaded" class="header-item header-item-quota">
                  <router-link :to="{ name: 'billing-info', params: { subId: billingAdmin } }">
                    <span v-tooltip="donutTooltip">
                      <vc-donut
                        background="var(--dark-primary)"
                        foreground="var(--accent-grey)"
                        :size="48"
                        :sections="sections"
                        :auto-adjust-text-size="true"
                      >
                        <h4 v-if="isSuspended" style="color: var(--fail)">
                          <i class="fa fa-ban" /><span class="sr-only">Suspended</span>
                        </h4>
                        <h4 v-else style="color: var(--light-primary)">
                          {{ sections[0].value }}%
                        </h4></vc-donut
                      >
                    </span>
                  </router-link>
                </div>
                <div v-if="$auth.isAuthenticated && $auth.user" class="header-item">
                  <div class="icon-background">
                    <a
                      href="https://apimetrics.readme.io/docs"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Docs"
                      class="docs-icon"
                    >
                      <i class="fa fa-question" />
                    </a>
                  </div>
                </div>
                <div v-if="$auth.isAuthenticated && $auth.user" class="dropdown header-item">
                  <a
                    id="project"
                    href="#"
                    role="button"
                    class="dropdown-toggle"
                    data-toggle="dropdown"
                    data-intercom-target="Topbar Account"
                  >
                    <div class="user-picture">
                      <img
                        :src="$auth.user.picture"
                        style="max-width: 140px"
                        alt="Profile picture"
                      />
                    </div>
                  </a>
                  <ul class="dropdown-menu" role="menu" aria-labelledby="project">
                    <li>
                      <router-link :to="{ name: 'settings-profile' }">
                        <i class="fa fa-user" /> {{ $auth.user.name }}
                      </router-link>
                    </li>
                    <li>
                      <router-link :to="{ name: 'billing-home' }">
                        <i class="glyphicon glyphicon-book" /> Subscriptions &amp; Billing
                      </router-link>
                    </li>
                    <li>
                      <router-link
                        :to="{ name: 'email-subscriptions' }"
                        data-intercom-target="Sidebar Email"
                      >
                        <i class="glyphicon glyphicon-envelope" /> Email Settings
                      </router-link>
                    </li>
                    <li v-if="sortedOrgs.length" class="divider" />
                    <li v-if="sortedOrgs.length">
                      <template v-for="org in sortedOrgs">
                        <router-link
                          v-if="org.id"
                          :key="org.id"
                          :to="{ name: 'organization-home', params: { orgId: org.id } }"
                        >
                          <i class="fa fa-users" />
                          {{ org.name }}
                        </router-link>
                      </template>
                    </li>
                    <li
                      v-if="isSiteViewer || hasQuotaViewPermissions"
                      class="divider dropdown-alert"
                    />
                    <li v-if="isSiteViewer" class="dropdown-alert">
                      <a :href="adminNotifsUrl"> Site Admin Alerts </a>
                    </li>
                    <li v-if="isSiteViewer" class="dropdown-alert">
                      <a :href="auth0ConsoleUrl" target="_blank" rel="noopener noreferrer">
                        Auth0 console
                      </a>
                    </li>
                    <li v-if="isSiteViewer || hasQuotaViewPermissions" class="dropdown-alert">
                      <router-link :to="{ name: 'accounts-admin-home' }"> Accounts... </router-link>
                    </li>
                    <li v-if="isSiteViewer || hasQuotaViewPermissions" class="dropdown-alert">
                      <router-link :to="{ name: 'orgs-admin-home' }">
                        Organizations...
                      </router-link>
                    </li>
                    <li v-if="isSiteAdmin" class="dropdown-alert">
                      <a href="/organizations/">Edit Organizations</a>
                    </li>
                    <li class="divider" />
                    <li>
                      <a href="http://apimetrics.io/contact/">
                        <i class="fa fa-envelope-o" /> Contact Us
                      </a>
                    </li>
                    <li>
                      <a @click.prevent="logout" @keyup.enter="logout"> Logout </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </li>

          <li v-if="!$auth.isAuthenticated">
            <a @click.prevent="doLogin" @keyup.enter="doLogin"> Login </a>
          </li>
          <li v-if="!$auth.isAuthenticated">
            <a @click.prevent="doSignUp" @keyup.enter="doSignUp"> Sign Up </a>
          </li>
          <li v-if="!$auth.isAuthenticated">
            <a href="http://apimetrics.io/pricing/">Pricing</a>
          </li>
          <li v-if="!$auth.isAuthenticated">
            <a href="http://apimetrics.io/contact/">Contact Us</a>
          </li>
          <li v-if="!$auth.isAuthenticated">
            <a href="https://apimetrics.readme.io/docs" target="_blank" rel="noopener noreferrer">
              Docs
            </a>
          </li>
        </ul>

        <ul v-if="$auth.loading" class="nav navbar-nav navbar-right">
          <li>
            <LoadingAnim no-text inline tag="span" />
          </li>
        </ul>
      </div>
      <!--/.nav-collapse -->
    </GridContainer>
  </nav>
</template>

<script>
import _sortBy from 'lodash/sortBy';
import { call, get } from 'vuex-pathify';
import UpgradeButton from './UpgradeButton.vue';
import delayLoad from '../delay';
import ProjectPicker from './ProjectPicker.vue';

const DEFAULT_PROJECT_ID = 'defaultProjectId';
const AUDIENCE = process.env.VUE_APP_AUTH0_AUDIENCE;
const AdminProjectPicker = delayLoad(() =>
  import(/* webpackChunkName: "AdminProjectPicker" */ './AdminProjectPicker.vue'),
);

export default {
  name: 'TopBar',
  components: {
    AdminProjectPicker,
    ProjectPicker,
    UpgradeButton,
  },
  data() {
    return {
      adminNotifsUrl:
        '/notifications/?action_user_key_str=ag9zfmFwaW1ldHJpY3MtcWNyEAsSBFVzZXIiBnB1YmxpYww',
      showUpgrade: true,
      favOrgs: [],
      favProjects: [],
    };
  },
  computed: {
    isSuspended() {
      return this.quotaInfo?.sub?.level === 'NONE';
    },
    sections() {
      if (this.isSuspended) {
        return [{ value: '100', color: 'var(--fail)' }];
      }
      const totalRun = this.quotaInfo?.counts.org_test_run_count || 0;
      const totalQuota = this.quotaInfo?.quotas.test_run_quota || 0;
      const percentage = ((totalRun / totalQuota) * 100).toFixed(0);
      return [
        { value: Math.min(parseInt(percentage, 10), 100), color: this.quotaColors(percentage) },
      ];
    },
    donutTooltip() {
      if (this.isSuspended) {
        return 'Project suspended, please contact support.';
      }
      return `${this.sections[0].value}% of your quota has been used this month. Click to see more details.`;
    },
    showFractal() {
      return this.orgId && this.orgTags.find((o) => o === 'api_expert:openbankingukproduction');
    },
    auth0ConsoleUrl() {
      return `https://manage.auth0.com/dashboard/us/${process.env.VUE_APP_AUTH0_TENANT_ID}/activity`;
    },
    billingAdmin() {
      return this.quotaInfo?.billing_admin_id || this.projectId;
    },
    sysTags() {
      if (this.projectId) {
        if (this.projectOrgId) {
          return this.projectOrgTags;
        }
        return this.projectSysTags;
      }
      return [];
    },
    getPath: get('nonVueRoutes/getPath'),
    organizations: get('account2/organizations'),
    roles: get('account2/roles'),
    loaded: get('account2/loaded'),
    quotaInfo: get('account2/quotaInfo'),
    projects: get('account2/projects'),
    projectsLoaded: get('account2/projectsLoaded'),
    projectId: get('account2/projectId'),
    projectSysTags: get('account2/projects[:projectId].project.system_tags'),
    projectName: get('account2/projects[:projectId].project.name'),
    projectOrgId: get('account2/projects[:projectId].project.org_id'),
    projectOrgName: get('account2/organizations[:projectOrgId].name'),
    projectOrgTags: get('account2/organizations[:projectOrgId].system_tags'),
    isSiteAdmin: get('account2/isSiteAdmin'),
    isSiteViewer: get('account2/isSiteViewer'),
    userRoles() {
      if (!this.$auth.loading && this.$auth.isAuthenticated && this.$auth.user) {
        return this.$auth.user[`${AUDIENCE}/permissions`];
      }
      return [];
    },
    hasQuotaViewPermissions() {
      return this.userRoles.includes('quota_viewer');
    },
    orgId() {
      return this.$route.params.orgId || this.projectOrgId;
    },
    orgTags() {
      if (this.orgId && this.organizations && this.organizations[this.orgId]) {
        return this.projectOrgTags;
      }
      return [];
    },
    sortedOrgs() {
      const orgIds = Object.keys(this.roles || {});
      const orgs = orgIds.map((orgId) => this.organizations[orgId]).filter((o) => o && o.id);
      return _sortBy(orgs, 'name');
    },
  },
  methods: {
    async fetchData() {
      const claims = await this.$auth.getIdTokenClaims();
      if (!claims) {
        console.log('No claims ', claims);
        return;
      }
      this.favProjects = claims[`${AUDIENCE}/fav_projects`];
      this.favOrgs = claims[`${AUDIENCE}/fav_orgs`];
    },
    async logout() {
      window.sessionStorage.removeItem(DEFAULT_PROJECT_ID);
      try {
        await this.$fetcher.logout().catch((ex) => {
          console.warn(ex);
        });
      } catch (ex) {
        console.warn(ex);
      }
      await this.$auth.logout({ returnTo: process.env.VUE_APP_AUTH0_REDIRECT_URI });
    },
    setShowUpgrade(value) {
      this.showUpgrade = value;
    },
    doLogin() {
      this.$cookies.set('continue-url', { path: this.$route.path }, '1h');
      this.$auth.loginWithRedirect();
    },
    doSignUp() {
      this.$cookies.set('continue-url', { path: this.$route.path }, '1h');
      this.$auth.loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } });
    },
    quotaColors(percentage) {
      if (percentage < 75) {
        return 'var(--pass)';
      }
      if (percentage < 90) {
        return 'var(--warn)';
      }
      return 'var(--fail)';
    },
    ...call('apimAPI', ['createCall']),
  },
};
</script>

<style scoped>
.grid-container {
  display: block;
  padding-top: 8px;
}

.navbar-default {
  min-height: 80px;
}

.navbar-nav {
  margin-top: 0px !important;
}

.navbar-fixed-top {
  position: sticky;
  margin-bottom: 0px;
}

.icon-background {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--light-primary);
}

.header-right-components {
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: center;
}

.nav-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 32px;
  padding-right: 12px;
  flex-wrap: wrap;
}

.user-picture {
  border-radius: 50%;
  overflow: hidden;
  width: 48px;
  height: 48px;
}
.user-picture img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.user-picture img:hover {
  cursor: pointer;
  opacity: 80%;
}
.sidebar {
  margin-bottom: -20px;
  background-color: transparent;
  box-shadow: none;
  -webkit-box-shadow: none;
  padding-top: 0;
  padding-bottom: 0;
}
.docs-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 35px;
  color: var(--lightpurple);
  text-decoration: none;
}
@media (max-width: 767px) {
  .nav-container {
    align-items: flex-start;
    padding: 8px;
  }
  .header-item {
    flex: 1;
    flex-basis: 100%;
  }
  .header-item-quota {
    display: none;
  }
  .docs-icon {
    justify-content: flex-start;
  }

  .navbar-nav {
    padding-left: 32px;
  }
}
.docs-icon :hover {
  color: var(--accent);
}

@media (min-width: 767px) {
  .nav-container {
    flex-direction: row;
  }
}
</style>
